<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import InputText from 'primevue/inputtext';
import AddIdentifierButton from "./AddIdentifierButton.vue";
import MaintenanceTable from "./MaintenanceTable.vue";
import type { MaintenanceData, MaintenanceLanguage } from "../../types/TextMaintenanceTypes";

const props = defineProps({
    selectedProduct: {
        type: String,
        required: true,
    },
    selectedLanguages: {
        type: Array as () => string[],
        required: true,
    },
    availableLanguages: {
        type: Array as () => MaintenanceLanguage[],
        required: true,
    },
    profileData: {
        type: Object as () => MaintenanceData,
        required: true,
    },
    questionnaireData: {
        type: Object as () => MaintenanceData,
        required: true,
    },
    defaultData: {
        type: Object as () => MaintenanceData,
        required: true,
    },
    hasDefaultTexts: {
        type: Boolean,
        default: false,
    },
    activeTabIndex: {
        type: Number,
        required: true,
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits([
    "tab-change",
    "delete-item",
    "update-item",
    "add-item"
]);

const { t } = useI18n();

// Search functionality
const searchTerm = ref('');

// Reflect only the real (store) loading state. Selecting/deselecting a language
// just re-filters already-loaded data, so there's no need for an artificial delay.
const isLoading = computed(() => props.loading);

const profileTabLabel = computed(() => 
    t('textMaintenance.tabs.profile', { product: props.selectedProduct })
);

const questionnaireTabLabel = computed(() => 
    t('textMaintenance.tabs.questionnaire', { product: props.selectedProduct })
);

const textsTabLabel = computed(() => 
    t('textMaintenance.tabs.texts', { product: props.selectedProduct })
);

// Filter data based on search term
const filteredProfileData = computed(() => {
    if (!searchTerm.value.trim()) {
        return props.profileData;
    }
    
    const filtered = {
        ...props.profileData,
        items: props.profileData.items.filter(item => 
            item.identifier.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
            Object.values(item.texts).some(text => 
                text.toLowerCase().includes(searchTerm.value.toLowerCase())
            )
        )
    };
    
    return filtered;
});

const filteredQuestionnaireData = computed(() => {
    if (!searchTerm.value.trim()) {
        return props.questionnaireData;
    }
    
    const filtered = {
        ...props.questionnaireData,
        items: props.questionnaireData.items.filter(item => 
            item.identifier.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
            Object.values(item.texts).some(text => 
                text.toLowerCase().includes(searchTerm.value.toLowerCase())
            )
        )
    };
    
    return filtered;
});

const filteredDefaultData = computed(() => {
    if (!searchTerm.value.trim()) {
        return props.defaultData;
    }
    
    const filtered = {
        ...props.defaultData,
        items: props.defaultData.items.filter(item => 
            item.identifier.toLowerCase().includes(searchTerm.value.toLowerCase()) ||
            Object.values(item.texts).some(text => 
                text.toLowerCase().includes(searchTerm.value.toLowerCase())
            )
        )
    };
    
    return filtered;
});

const handleTabChange = (newTabIndex: string | number) => {
    const tabIndex = typeof newTabIndex === 'string' ? Number.parseInt(newTabIndex, 10) : newTabIndex;
    emit("tab-change", tabIndex);
};

const handleDeleteItem = (itemId: string) => {
    emit("delete-item", itemId);
};

const handleUpdateItem = (updatedItem: any) => {
    emit("update-item", updatedItem);
};

const handleAddItem = () => {
    emit("add-item");
};
</script>

<template>
    <div v-if="selectedProduct && selectedLanguages.length > 0">
        <!-- Tabs for Profile and Questionnaire with Search and Add Identifier Button -->
        <div class="flex justify-between items-center mb-4">
            <Tabs :value="activeTabIndex" @update:value="handleTabChange" class="grow">
                <TabList>
                    <!-- Show profile and questionnaire tabs if we don't have default texts -->
                    <template v-if="!hasDefaultTexts">
                        <Tab :value="0" data-cy="text-maintenance_profile-tab">{{ profileTabLabel }}</Tab>
                        <Tab :value="1" data-cy="text-maintenance_questionnaire-tab">{{ questionnaireTabLabel }}</Tab>
                    </template>
                    <!-- Show only texts tab if we have default texts -->
                    <template v-else>
                        <Tab :value="2" data-cy="text-maintenance_texts-tab">{{ textsTabLabel }}</Tab>
                    </template>
                </TabList>
            </Tabs>
            
            <!-- Search Input -->
            <InputText
                data-cy="text-maintenance_identifier-search"
                v-model="searchTerm"
                :placeholder="t('textMaintenance.searchIdentifiers')"
                class="ml-4"
                style="width: 400px;"
            />
            
            <!-- Add Identifier Button -->
            <AddIdentifierButton
                size="small"
                :disabled="isLoading"
                class="ml-2"
                @add-item="handleAddItem"
                data-cy="text-maintenance_add-identifier-button-header"
            />
        </div>
        
        <Tabs :value="activeTabIndex" @update:value="handleTabChange">            
            <TabPanels>
                <!-- Profile Tab (only if no default texts) -->
                <TabPanel v-if="!hasDefaultTexts" :value="0">
                    <MaintenanceTable
                        v-if="!isLoading"
                        :data="filteredProfileData.items"
                        :selected-languages="selectedLanguages"
                        :available-languages="availableLanguages"
                        :loading="isLoading"
                        @delete-item="handleDeleteItem"
                        @update-item="handleUpdateItem"
                    />
                    <div v-else class="text-center py-20">
                        <i class="pi pi-spin pi-spinner text-4xl text-content-muted mb-4"></i>
                        <p class="text-content-muted">Loading profile data...</p>
                    </div>
                </TabPanel>
                
                <!-- Questionnaire Tab (only if no default texts) -->
                <TabPanel v-if="!hasDefaultTexts" :value="1">
                    <MaintenanceTable
                        v-if="!isLoading"
                        :data="filteredQuestionnaireData.items"
                        :selected-languages="selectedLanguages"
                        :available-languages="availableLanguages"
                        :loading="isLoading"
                        @delete-item="handleDeleteItem"
                        @update-item="handleUpdateItem"
                    />
                    <div v-else class="text-center py-20">
                        <i class="pi pi-spin pi-spinner text-4xl text-content-muted mb-4"></i>
                        <p class="text-content-muted">Loading questionnaire data...</p>
                    </div>
                </TabPanel>

                <!-- Default Texts Tab (only if default texts exist) -->
                <TabPanel v-if="hasDefaultTexts" :value="2">
                    <MaintenanceTable
                        v-if="!isLoading"
                        :data="filteredDefaultData.items"
                        :selected-languages="selectedLanguages"
                        :available-languages="availableLanguages"
                        :loading="isLoading"
                        @delete-item="handleDeleteItem"
                        @update-item="handleUpdateItem"
                    />
                    <div v-else class="text-center py-20">
                        <i class="pi pi-spin pi-spinner text-4xl text-content-muted mb-4"></i>
                        <p class="text-content-muted">Loading default texts...</p>
                    </div>
                </TabPanel>
            </TabPanels>
        </Tabs>
    </div>

    <!-- Empty State -->
    <div v-else class="text-center py-20">
        <i class="pi pi-info-circle text-4xl text-content-muted mb-4"></i>
        <h3 class="text-content-muted mb-2">{{ t("textMaintenance.emptyState.title") }}</h3>
        <p class="text-content-muted">{{ t("textMaintenance.emptyState.description") }}</p>
    </div>
</template>

<style scoped>
</style>